<template>
  <div class="productslist container-fluid">
    <!-- LOADING -->
    <transition name="fade">
      <div
        v-if="loading"
        class="loading-overlay d-flex align-items-center justify-content-center"
      >
        <Loading />
      </div>
    </transition>

    <!-- HEADER (sem selector de site) -->
    <div class="header py-4">
      <div class="container-tight">
        <div
          class="d-flex flex-wrap align-items-center justify-content-between"
        >
          <div class="d-flex align-items-center hero-left">
            <div>
              <h1 class="m-0 page-title">Feeds</h1>
              <small class="text-muted"
                >Gestão centralizada de feeds, categorias e moedas</small
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- NAV (menu principal centrado) -->
    <div class="container-tight">
      <div class="segmented p-2 mx-auto segmented-wide">
        <b-button
          class="segmented-item"
          :class="{ active: page === 'feeds' }"
          @click="page = 'feeds'"
          >Feeds</b-button
        >
        <b-button
          class="segmented-item"
          :class="{ active: page === 'categories' }"
          @click="page = 'categories'"
          >Categorias</b-button
        >
        <b-button
          class="segmented-item"
          :class="{ active: page === 'coins' }"
          @click="page = 'coins'"
          >Moedas</b-button
        >
      </div>
    </div>

    <!-- ACTIONS -->
    <div class="container-tight">
      <div
        class="d-flex gap-2 justify-content-center mb-3"
        v-if="page === 'feeds'"
      >
        <b-button variant="primary" @click="openFeedModal()"
          >Adicionar Feed</b-button
        >
        <b-button variant="outline-primary" @click="openCustomValuesModal()"
          >Custom Labels</b-button
        >
        <b-button variant="success" @click="generateFeeds()"
          >Gerar Feeds</b-button
        >
      </div>
      <div
        class="d-flex gap-2 justify-content-center mb-3"
        v-else-if="page === 'categories'"
      >
        <b-button variant="primary" @click="openCategoryModal()"
          >Adicionar Categoria</b-button
        >
        <b-button variant="outline-primary" @click="openCategorySKUModal()"
          >Atualizar via SKU</b-button
        >
      </div>
    </div>

    <!-- FEEDS full-width -->
    <div class="container-wide" v-if="page === 'feeds'">
      <b-card class="shadow-sm feeds-card">
        <!-- Header do card: plataforma + título + filtro + selector de site -->
        <div class="feeds-card__header">
          <div class="fch-left">
            <div class="badge-soft mr-2 text-uppercase">
              {{ isShopify ? 'Shopify' : 'WooCommerce' }}
            </div>
            <h3 class="m-0 feeds-card__title">Lista de Feeds</h3>
          </div>

          <div class="fch-right">
            <label class="mb-0 mr-2 font-weight-bold d-none d-md-inline"
              >Site</label
            >
            <b-form-select
              v-model="current_site"
              :options="siteOptions"
              class="w-auto"
              @change="reloadAll"
            />
          </div>
        </div>

        <div class="table-responsive mt-2">
          <table class="table table-hover table-sm align-middle feeds-table">
            <thead class="thead-light">
              <tr>
                <th @click="toggleFeedsSort" class="th-sort w-id">
                  ID
                  <span class="sort-caret">{{ sortFeedsAsc ? '▲' : '▼' }}</span>
                </th>
                <th class="w-name">Feed</th>
                <th class="w-chips">Países</th>
                <th class="w-chips">Cat. Excluídas</th>
                <th class="w-xs">Língua</th>
                <th class="w-xs">Stock</th>
                <th class="w-xs">Round</th>
                <th class="w-xs">Free Ship</th>
                <th class="w-xs">Vendas</th>
                <th class="w-sm">Data Vendas</th>
                <th v-if="isShopify" class="w-sm">Handle</th>
                <th v-if="isShopify" class="w-xs">Adjust</th>
                <th class="text-right w-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in feedsSorted" :key="f.id" class="feed-row">
                <td class="text-mono">{{ f.id }}</td>

                <td>
                  <div class="d-flex align-items-center">
                    <div class="feed-avatar mr-2">{{ (f.name || 'F')[0] }}</div>
                    <div>
                      <div class="font-weight-bold text-truncate">
                        {{ f.name }}
                      </div>
                      <div class="d-flex align-items-center small text-muted">
                        <b-button
                          size="sm"
                          variant="light"
                          class="btn-ghost px-2 py-1 mr-1"
                          :href="feedLinkForRow(f)"
                          target="_blank"
                          rel="noopener"
                          title="Abrir feed"
                        >
                          🔗 Ver feed
                        </b-button>
                        <span class="dot-sep">•</span>
                        <span class="ml-1">XML</span>
                      </div>
                    </div>
                  </div>
                </td>

                <td class="cell-chips">
                  <span
                    v-for="(c, i) in Array.isArray(f.countrys)
                      ? f.countrys.slice(0, 3)
                      : []"
                    :key="i"
                    class="chip chip-soft"
                    :title="c"
                  >
                    {{ c }}
                  </span>
                  <span
                    v-if="Array.isArray(f.countrys) && f.countrys.length > 3"
                    class="chip chip-more"
                    :title="(f.countrys || []).join(', ')"
                  >
                    +{{ f.countrys.length - 3 }}
                  </span>
                </td>

                <td class="cell-chips">
                  <span
                    v-for="(c, i) in Array.isArray(f.excluded_categories)
                      ? f.excluded_categories.slice(0, 3)
                      : []"
                    :key="i"
                    class="chip chip-outline"
                    :title="c"
                  >
                    {{ c }}
                  </span>
                  <span
                    v-if="
                      Array.isArray(f.excluded_categories) &&
                      f.excluded_categories.length > 3
                    "
                    class="chip chip-more"
                    :title="(f.excluded_categories || []).join(', ')"
                  >
                    +{{ f.excluded_categories.length - 3 }}
                  </span>
                </td>

                <td class="text-mono">{{ f.language }}</td>
                <td>
                  <span
                    :class="['pill', f.only_stock ? 'pill-green' : 'pill-gray']"
                  >
                    {{ f.only_stock ? 'Sim' : 'Não' }}
                  </span>
                </td>
                <td class="text-mono">{{ f.round }}</td>
                <td class="text-mono">{{ f.free_shipping }}</td>
                <td class="text-mono">{{ f.vendas }}</td>
                <td class="text-mono">{{ f.date_vendas }}</td>
                <td
                  v-if="isShopify"
                  class="text-truncate"
                  :title="f.handle || '-'"
                >
                  {{ f.handle || '-' }}
                </td>
                <td v-if="isShopify" class="text-mono">{{ f.adjust ?? 0 }}</td>

                <td class="text-right">
                  <div class="btn-group btn-group-sm">
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      @click="passFeedData(f)"
                      title="Editar"
                      class="btn-square"
                      >✏️</b-button
                    >
                    <b-button
                      size="sm"
                      variant="outline-danger"
                      @click="deleteFeed(f)"
                      title="Eliminar"
                      class="btn-square"
                      >🗑️</b-button
                    >
                  </div>
                </td>
              </tr>

              <tr v-if="!feedsSorted.length">
                <td :colspan="isShopify ? 13 : 11" class="py-5">
                  <div class="empty-state">
                    <div class="empty-ico">🗂️</div>
                    <div class="empty-title">Ainda não existem feeds</div>
                    <div class="empty-sub">
                      Clica em <strong>Adicionar Feed</strong> para criar o
                      primeiro.
                    </div>
                    <b-button
                      variant="primary"
                      class="mt-2"
                      @click="openFeedModal()"
                      >Adicionar Feed</b-button
                    >
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>
    </div>

    <!-- CATEGORIES (mantido) -->
    <div class="container-tight" v-else-if="page === 'categories'">
      <b-card class="shadow-sm">
        <div class="table-responsive">
          <table class="table table-hover table-sm align-middle">
            <thead class="thead-light">
              <tr>
                <th
                  style="width: 100px"
                  @click="toggleCategoriesSort"
                  class="th-sort"
                >
                  ID
                  <span class="sort-caret">{{
                    sortCategoriesAsc ? '▲' : '▼'
                  }}</span>
                </th>
                <th>Nome</th>
                <th class="text-right" style="width: 200px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in categoriesSorted" :key="c.id">
                <td>{{ c.id }}</td>
                <td>{{ c.name }}</td>
                <td class="text-right">
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    @click="updateFile(c)"
                    title="Atualizar via CSV"
                    >🔧</b-button
                  >
                  <b-button
                    size="sm"
                    variant="outline-info"
                    class="ml-1"
                    @click="downloadProducts(c)"
                    title="Exportar CSV"
                    >⬇️</b-button
                  >
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    class="ml-1"
                    @click="deleteCategory(c)"
                    title="Eliminar"
                    >❌</b-button
                  >
                </td>
              </tr>
              <tr v-if="!categoriesSorted.length">
                <td colspan="3" class="text-center text-muted py-4">
                  Sem dados
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>
    </div>

    <!-- COINS (mantido) -->
    <div class="container-tight" v-else>
      <b-card class="shadow-sm">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Moedas</h5>

          <b-button
            variant="primary"
            size="sm"
            @click="$refs['modal_add-coin'].show()"
          >
            + Adicionar Moeda
          </b-button>
        </div>

        <div class="table-responsive">
          <table class="table table-hover table-sm align-middle">
            <thead class="thead-light">
              <tr>
                <th>Moeda</th>
                <th style="width: 200px">Câmbio</th>
                <th class="text-right" style="width: 120px"></th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="c in coinsFiltered" :key="c.coin">
                <td>{{ c.coin }}</td>
                <td><b-form-input v-model="c.rate" size="sm" /></td>
                <td class="text-right">
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    @click="updateCoins(c)"
                  >
                    💾 Guardar
                  </b-button>
                </td>
              </tr>

              <tr v-if="!coinsFiltered.length">
                <td colspan="3" class="text-center text-muted py-4">
                  Sem dados
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>
    </div>

    <!-- MODALS (mantidos) -->
    <b-modal
      id="modal_add-feed"
      title="Adicionar Feed"
      ref="modal_add-feed"
      hide-footer
    >
      <b-form @submit.prevent="addFeed">
        <b-form-group label="Site do feed">
          <b-form-select v-model="new_site" :options="siteOptions" />
        </b-form-group>

        <b-form-group label="Nome"
          ><b-form-input v-model="new_name" required
        /></b-form-group>

        <b-form-group label="Países">
          <v-select
            :options="countries"
            v-model="new_country"
            label="name"
            multiple
          >
            <template #option="{ name }">{{ name }}</template>
          </v-select>
        </b-form-group>

        <b-form-group label="Categorias Excluídas">
          <v-select
            :options="categories"
            v-model="new_categories"
            label="name"
            multiple
          />
        </b-form-group>

        <b-form-group label="Língua"
          ><v-select
            :options="languagesForSite(new_site)"
            v-model="new_language"
        /></b-form-group>
        <b-form-group label="Arredondar"
          ><v-select :options="roundsForSite(new_site)" v-model="new_round"
        /></b-form-group>

        <b-form-group label="Free Shipping"
          ><b-form-input type="number" v-model="new_free_shipping"
        /></b-form-group>
        <b-form-group label="Vendas"
          ><b-form-input type="number" v-model="new_vendas"
        /></b-form-group>
        <b-form-group label="Data Vendas"
          ><b-form-input type="number" v-model="new_date_vendas"
        /></b-form-group>

        <b-form-checkbox v-model="new_only_stock" switch
          >Apenas Stock</b-form-checkbox
        >

        <template v-if="sites[new_site]?.type === 'shopify'">
          <hr />
          <b-form-group label="Handle (Shopify)"
            ><b-form-input v-model="new_handle"
          /></b-form-group>
          <b-form-group label="Adjust (Shopify)"
            ><b-form-input type="number" v-model.number="new_adjust"
          /></b-form-group>
        </template>

        <b-button type="submit" variant="primary" block class="mt-3"
          >Adicionar</b-button
        >
      </b-form>
    </b-modal>

    <b-modal
      id="modal_up-feed"
      title="Atualizar Feed"
      ref="modal_up-feed"
      hide-footer
    >
      <b-form @submit.prevent="updateFeed">
        <b-form-group label="Nome"
          ><b-form-input v-model="up_name" required
        /></b-form-group>

        <b-form-group label="Países">
          <v-select
            :options="countries"
            v-model="up_country"
            label="name"
            multiple
          />
        </b-form-group>

        <b-form-group label="Categorias Excluídas">
          <v-select
            :options="categories"
            v-model="up_categories"
            label="name"
            multiple
          />
        </b-form-group>

        <b-form-group label="Língua"
          ><v-select :options="languagesActive" v-model="up_language"
        /></b-form-group>
        <b-form-group label="Arredondar"
          ><v-select :options="roundsActive" v-model="up_round"
        /></b-form-group>

        <b-form-group label="Free Shipping"
          ><b-form-input type="number" v-model="up_free_shipping"
        /></b-form-group>
        <b-form-group label="Vendas"
          ><b-form-input type="number" v-model="up_vendas"
        /></b-form-group>
        <b-form-group label="Data Vendas"
          ><b-form-input type="number" v-model="up_date_vendas"
        /></b-form-group>

        <b-form-checkbox v-model="up_only_stock" switch
          >Apenas Stock</b-form-checkbox
        >

        <template v-if="isShopify">
          <hr />
          <b-form-group label="Handle (Shopify)"
            ><b-form-input v-model="up_handle"
          /></b-form-group>
          <b-form-group label="Adjust (Shopify)"
            ><b-form-input type="number" v-model.number="up_adjust"
          /></b-form-group>
        </template>

        <b-button type="submit" variant="primary" block class="mt-3"
          >Atualizar</b-button
        >
      </b-form>
    </b-modal>

    <b-modal
      id="modal_add-category"
      title="Adicionar Categoria"
      ref="modal_add-category"
      hide-footer
    >
      <b-form @submit.prevent="addCategory">
        <b-form-group label="Nome"
          ><b-form-input v-model="new_name_cat" required
        /></b-form-group>
        <b-button type="submit" variant="primary" block>Adicionar</b-button>
      </b-form>
    </b-modal>

    <b-modal
      id="modal_add-category-sku"
      title="Atualizar Categorias via SKU"
      ref="modal_add-category-sku"
      hide-footer
    >
      <b-form @submit.prevent="updateViaSKU">
        <b-form-group label="SKUs (separados por vírgulas)"
          ><b-form-input v-model="up_skus" required
        /></b-form-group>
        <b-form-group label="Categorias">
          <v-select
            :options="categories"
            v-model="up_categories_sku"
            label="name"
            multiple
          />
        </b-form-group>
        <b-button type="submit" variant="primary" block>Atualizar</b-button>
      </b-form>
    </b-modal>

    <b-modal
      id="modal_add-prod"
      size="xl"
      title="Atualizar Produtos Categoria"
      ref="modal_add-prod"
      hide-footer
    >
      <b-form @submit.prevent="updateCategoryProducts">
        <div class="custom-file mb-2">
          <input
            type="file"
            class="custom-file-input"
            id="customFile"
            ref="file"
            @change="onFileChange"
            accept=".csv"
          />
          <label class="custom-file-label" for="customFile">{{
            fileName
          }}</label>
        </div>
        <b-button type="submit" variant="primary" block>Atualizar</b-button>
      </b-form>
    </b-modal>

    <b-modal
      id="modal_add-custom-values"
      title="Custom Labels"
      ref="modal_add-custom-values"
      hide-footer
      size="lg"
    >
      <div class="table-responsive">
        <table class="table table-sm mb-0">
          <thead class="thead-light">
            <tr>
              <th>Nome</th>
              <th>Min</th>
              <th>Med</th>
              <th>Max</th>
              <th>Peso</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="label in custom_values" :key="label.name">
              <td class="align-middle">{{ label.name }}</td>
              <td>
                <b-form-input
                  type="number"
                  v-model.number="label.min"
                  size="sm"
                />
              </td>
              <td>
                <b-form-input
                  type="number"
                  v-model.number="label.med"
                  size="sm"
                />
              </td>
              <td>
                <b-form-input
                  type="number"
                  v-model.number="label.max"
                  size="sm"
                />
              </td>
              <td>
                <b-form-input
                  type="number"
                  v-model.number="label.weight"
                  size="sm"
                />
              </td>
              <td class="text-nowrap">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  @click="updateCustomValues(label)"
                  >🔄</b-button
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
    <b-modal
      id="modal_add-coin"
      title="Adicionar Moeda"
      ref="modal_add-coin"
      hide-footer
    >
      <b-form @submit.prevent="addCoin">
        <b-form-group label="Moeda (ex: GBP)" label-for="new_coin_code">
          <b-form-input
            id="new_coin_code"
            v-model="new_coin.coin"
            required
            placeholder="GBP"
          />
        </b-form-group>

        <b-form-group label="Câmbio (rate)" label-for="new_coin_rate">
          <b-form-input
            id="new_coin_rate"
            type="number"
            step="0.000001"
            v-model.number="new_coin.rate"
            required
            placeholder="1.000000"
          />
        </b-form-group>

        <b-button type="submit" variant="primary" block class="mt-3">
          Adicionar
        </b-button>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import Loading from '@/components/Loading.vue';

export default {
  components: { Loading },

  data() {
    return {
      // multi-site
      current_site: 'idivia.com',
      sites: {
        'idivia.com': { type: 'woocommerce' },
        'idivia.eu': { type: 'shopify' },
      },

      // estado
      page: 'feeds',
      loading: false,
      filter: null,

      // sort toggles
      sortFeedsAsc: true,
      sortCategoriesAsc: true,

      // dados
      feeds: [],
      categories: [],
      countries: [],
      coins: [],
      custom_values: [],

      // opções por plataforma
      language_woo: ['PT', 'ES', 'JP', 'EN'],
      round_woo: ['CIMA', 'BAIXO', 'NORMAL'],
      language_shopify: ['PT', 'ES', 'EN'],
      round_shopify: ['NORMAL', 'CIMA', 'BAIXO'],

      // FEED ADD
      new_site: 'idivia.com',
      new_name: '',
      new_country: [],
      new_categories: [],
      new_language: '',
      new_round: '',
      new_only_stock: false,
      new_free_shipping: '',
      new_vendas: '',
      new_date_vendas: '',
      new_handle: '',
      new_adjust: 0,

      // FEED UPDATE
      up_id: '',
      up_name: '',
      up_country: [],
      up_categories: [],
      up_language: '',
      up_round: '',
      up_only_stock: false,
      up_free_shipping: '',
      up_vendas: '',
      up_date_vendas: '',
      up_handle: '',
      up_adjust: 0,

      // CATEGORY
      new_name_cat: '',

      // CSV
      file: '',
      fileName: 'Escolha um ficheiro',
      fileType: '',

      // SKU
      up_skus: '',
      up_categories_sku: [],

      new_coin: {
        coin: '',
        rate: 1,
      },
    };
  },

  computed: {
    siteOptions() {
      return Object.keys(this.sites).map((k) => ({ value: k, text: k }));
    },
    activeType() {
      return this.sites[this.current_site]?.type || '';
    },
    isShopify() {
      return this.activeType === 'shopify';
    },
    languagesActive() {
      return this.isShopify ? this.language_shopify : this.language_woo;
    },
    roundsActive() {
      return this.isShopify ? this.round_shopify : this.round_woo;
    },

    // filtros
    feedsFiltered() {
      if (!this.filter) return this.feeds;
      const q = this.filter.toLowerCase();
      return this.feeds.filter(
        (f) =>
          String(f.id).includes(q) ||
          (f.name || '').toLowerCase().includes(q) ||
          (f.language || '').toLowerCase().includes(q)
      );
    },
    categoriesFiltered() {
      if (!this.filter) return this.categories;
      const q = this.filter.toLowerCase();
      return this.categories.filter(
        (c) =>
          String(c.id).includes(q) || (c.name || '').toLowerCase().includes(q)
      );
    },
    coinsFiltered() {
      if (!this.filter) return this.coins;
      const q = this.filter.toLowerCase();
      return this.coins.filter((c) => (c.coin || '').toLowerCase().includes(q));
    },

    // ordenações
    feedsSorted() {
      const arr = [...this.feedsFiltered];
      arr.sort((a, b) => (this.sortFeedsAsc ? a.id - b.id : b.id - a.id));
      return arr;
    },
    categoriesSorted() {
      const arr = [...this.categoriesFiltered];
      arr.sort((a, b) => (this.sortCategoriesAsc ? a.id - b.id : b.id - a.id));
      return arr;
    },
  },

  methods: {
    toggleFeedsSort() {
      this.sortFeedsAsc = !this.sortFeedsAsc;
    },
    toggleCategoriesSort() {
      this.sortCategoriesAsc = !this.sortCategoriesAsc;
    },

    languagesForSite(site) {
      return this.sites[site]?.type === 'shopify'
        ? this.language_shopify
        : this.language_woo;
    },
    roundsForSite(site) {
      return this.sites[site]?.type === 'shopify'
        ? this.round_shopify
        : this.round_woo;
    },

    asList(v) {
      if (Array.isArray(v)) return v.join(', ');
      return v ?? '';
    },

    // FEED LINK:
    // - WooCommerce: <name>.xml
    // - Shopify: <handle>.xml (fallback para name)
    feedLinkForRow(row) {
      if (this.isShopify) {
        // Shopify → usa sempre o nome + "_shopify.xml"
        const baseName = row.name || 'feed';
        console.log(
          `Feed link (Shopify) for row ${row.id}: ${baseName}_shopify.xml`
        );
        return `https://backoffice.searchmatters.pt/feedsCountry/${baseName}_shopify.xml`;
      } else {
        // WooCommerce → só o nome
        const baseName = row.name || 'feed';
        console.log(
          `Feed link (WooCommerce) for row ${row.id}: ${baseName}.xml`
        );
        return `https://backoffice.searchmatters.pt/feedsCountry/${baseName}.xml`;
      }
    },

    onFileChange() {
      const f = this.$refs.file?.files?.[0];
      if (!f) return;
      this.file = f;
      this.fileName = f.name;
      this.fileType = f.type;
    },
    openFeedModal() {
      this.new_site = this.current_site;
      this.$refs['modal_add-feed'].show();
    },
    openCategoryModal() {
      this.$refs['modal_add-category'].show();
    },
    openCategorySKUModal() {
      this.$refs['modal_add-category-sku'].show();
    },

    async reloadAll() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetchFeeds(),
          this.fetchCategories(),
          this.fetchCountries(),
          this.fetchCoins(),
          this.fetchCustomLabels(),
        ]);
      } finally {
        this.loading = false;
      }
    },

    // ================= FEEDS =================
    async fetchFeeds() {
      this.feeds = [];
      if (this.activeType == 'woocommerce') {
        try {
          const { data } = await axios.get(
            `${this.$store.state.apiUrl}feedCountry/feeds`
          );
          this.feeds = data.data || [];
        } catch (e) {
          console.error('Error fetching feeds:', e);
        }
      } else if (this.activeType == 'shopify') {
        try {
          const { data } = await axios.get(
            `${this.$store.state.apiUrl}shopify/feeds/${this.current_site}`
          );
          this.feeds = data.data || [];
        } catch (e) {
          console.error('Error fetching feeds:', e);
        }
      }
    },

    async addFeed() {
      try {
        const selectedType = this.sites[this.new_site]?.type;
        const payload = {
          name: this.new_name,
          countrys: this.new_country,
          excluded_categories: this.new_categories,
          language: this.new_language,
          round: this.new_round,
          only_stock: this.new_only_stock,
          free_shipping: this.new_free_shipping,
          vendas: this.new_vendas,
          date_vendas: this.new_date_vendas,
        };
        if (selectedType === 'shopify') {
          payload.handle = this.new_handle;
          payload.adjust = this.new_adjust ?? 0;
          payload.site = this.new_site;
        }

        if (selectedType === 'woocommerce') {
          await axios.post(
            `${this.$store.state.apiUrl}feedCountry/feeds`,
            payload
          );
        } else if (selectedType === 'shopify') {
          await axios.post(
            `${this.$store.state.apiUrl}shopify/feeds/${this.current_site}`,
            payload
          );
        }

        this.$swal({ title: 'Feed Adicionado!', icon: 'success', timer: 1600 });

        Object.assign(this, {
          new_site: this.current_site,
          new_name: '',
          new_country: [],
          new_categories: [],
          new_language: '',
          new_round: '',
          new_only_stock: false,
          new_free_shipping: '',
          new_vendas: '',
          new_date_vendas: '',
          new_handle: '',
          new_adjust: 0,
        });

        await this.fetchFeeds();
        this.$refs['modal_add-feed'].hide();
      } catch (e) {
        console.error('Error adding feed:', e);
      }
    },

    passFeedData(f) {
      const feed = this.feeds.find((x) => x.id === f.id);
      if (!feed) return;
      this.up_id = feed.id;
      this.up_name = feed.name;
      this.up_country = (feed.countrys || [])
        .map((code) => this.countries.find((c) => c.code === code))
        .filter(Boolean);
      this.up_categories = (feed.excluded_categories || [])
        .map((cat) => this.categories.find((c) => c.name === cat))
        .filter(Boolean);
      this.up_language = feed.language || '';
      this.up_round = feed.round || '';
      this.up_only_stock = !!feed.only_stock;
      this.up_free_shipping = Number(feed.free_shipping || 0);
      this.up_vendas = Number(feed.vendas || 0);
      this.up_date_vendas = feed.date_vendas ?? '';
      this.up_handle = feed.handle || '';
      this.up_adjust = Number(feed.adjust ?? 0);
      this.$refs['modal_up-feed'].show();
    },

    async updateFeed() {
      try {
        const payload = {
          name: this.up_name,
          countrys: this.up_country,
          excluded_categories: this.up_categories,
          language: this.up_language,
          round: this.up_round,
          only_stock: this.up_only_stock,
          free_shipping: this.up_free_shipping,
          vendas: this.up_vendas,
          date_vendas: this.up_date_vendas,
        };
        if (this.isShopify) {
          payload.handle = this.up_handle;
          payload.adjust = this.up_adjust ?? 0;
        }

        if (this.activeType == 'woocommerce') {
          await axios.patch(`${this.$store.state.apiUrl}feedCountry/feeds`, {
            id: this.up_id,
            ...payload,
          });
        } else if (this.activeType == 'shopify') {
          await axios.patch(
            `${this.$store.state.apiUrl}shopify/feeds/${this.current_site}`,
            { id: this.up_id, ...payload }
          );
        }

        this.$swal({ title: 'Feed Atualizado!', icon: 'success', timer: 1600 });

        Object.assign(this, {
          up_id: '',
          up_name: '',
          up_country: [],
          up_categories: [],
          up_language: '',
          up_round: '',
          up_only_stock: false,
          up_free_shipping: '',
          up_vendas: '',
          up_date_vendas: '',
          up_handle: '',
          up_adjust: 0,
        });

        await this.fetchFeeds();
        this.$refs['modal_up-feed'].hide();
      } catch (e) {
        console.error('Error updating feed:', e);
      }
    },

    async deleteFeed(f) {
      this.$swal({
        title: `Eliminar o feed ${f.id}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, eliminar',
        cancelButtonText: 'Cancelar',
      }).then(async (r) => {
        if (!r.isConfirmed) return;
        try {
          if (this.activeType === 'woocommerce') {
            await axios.delete(`${this.$store.state.apiUrl}feedCountry/feeds`, {
              data: { id: f.id },
            });
          } else if (this.activeType === 'shopify') {
            await axios.delete(
              `${this.$store.state.apiUrl}shopify/feeds/${this.current_site}`,
              {
                data: { id: f.id },
              }
            );
          }
          await this.fetchFeeds();
          this.$swal('Completado', 'Feed eliminado!', 'success');
        } catch (e) {
          console.error(e);
          this.$swal?.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Não foi possível eliminar',
          });
        }
      });
    },

    // ============== CATEGORIES ==============
    async fetchCategories() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}feedCountry/categories`
        );
        this.categories = data.data || [];
      } catch (e) {
        console.error('Error fetching categories:', e);
      }
    },

    async addCategory() {
      try {
        await axios.post(`${this.$store.state.apiUrl}feedCountry/categories`, {
          name: this.new_name_cat,
        });
        this.$swal({
          title: 'Categoria Adicionada!',
          icon: 'success',
          timer: 1500,
        });
        this.new_name_cat = '';
        await this.fetchCategories();
        this.$refs['modal_add-category'].hide();
      } catch (e) {
        console.error('Error adding category:', e);
      }
    },

    async updateViaSKU() {
      const categories = this.up_categories_sku.map((cat) => cat.id);
      const skuArray = this.up_skus
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (!skuArray.length) return;
      try {
        await axios.post(`${this.$store.state.apiUrl}feedCountry/update_sku`, {
          sku: skuArray,
          categories,
        });
        this.$swal({
          title: 'Categorias Atualizadas!',
          icon: 'success',
          timer: 1500,
        });
        this.up_skus = '';
        this.up_categories_sku = [];
        this.$refs['modal_add-category-sku'].hide();
        await this.fetchCategories();
      } catch (e) {
        console.error('Error updating categories via SKU:', e);
      }
    },

    async deleteCategory(row) {
      this.$swal({
        title: `Eliminar a categoria ${row.id}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, eliminar',
        cancelButtonText: 'Cancelar',
      }).then(async (r) => {
        if (!r.isConfirmed) return;
        try {
          await axios.delete(
            `${this.$store.state.apiUrl}feedCountry/categories`,
            {
              data: { id: row.id },
            }
          );
          await this.fetchCategories();
          this.$swal('Completado', 'Categoria eliminada!', 'success');
        } catch (e) {
          console.error(e);
          this.$swal?.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Não foi possível eliminar',
          });
        }
      });
    },

    async downloadProducts(row) {
      try {
        const res = await axios.get(
          `${this.$store.state.apiUrl}feedCountry/categories/${row.id}`,
          {
            responseType: 'blob',
          }
        );
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${row.name}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Error downloading products:', e);
        this.$swal?.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível descarregar o CSV',
        });
      }
    },

    updateFile(row) {
      this.up_id = row.id;
      this.$refs['modal_add-prod'].show();
    },

    async updateCategoryProducts() {
      if (!this.file)
        return this.$swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Seleciona um ficheiro',
        });
      if (
        this.fileType !== 'text/csv' &&
        !this.file.name?.toLowerCase().endsWith('.csv')
      )
        return this.$swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Seleciona um ficheiro CSV',
        });

      try {
        const formData = new FormData();
        formData.append('csv', this.file);
        await axios.post(
          `${this.$store.state.apiUrl}feedCountry/categories/${this.up_id}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        this.$swal({
          title: 'Ficheiro Enviado!',
          icon: 'success',
          timer: 1500,
        });
        this.file = '';
        this.fileName = 'Escolha um ficheiro';
        this.fileType = '';
        this.$refs['modal_add-prod'].hide();
      } catch (e) {
        console.error('Error updating category products:', e);
      }
    },

    // ================ COINS ================
    async fetchCoins() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}feedCountry/coins`
        );
        this.coins = data.data || [];
      } catch (e) {
        console.error('Error fetching coins:', e);
      }
    },
    async updateCoins(item) {
      try {
        await axios.patch(`${this.$store.state.apiUrl}feedCountry/coins`, {
          coin: item.coin,
          rate: item.rate,
        });
        this.$swal({
          title: 'Moedas Atualizadas!',
          icon: 'success',
          timer: 1200,
        });
        await this.fetchCoins();
      } catch (e) {
        console.error('Error updating coins:', e);
      }
    },
    async confirmAdd(message) {
      const result = await this.$swal.fire({
        title: 'Adicionar?',
        text: message,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return result.isConfirmed;
    },
    async addCoin() {
      const coin = (this.new_coin.coin || '').trim().toUpperCase();
      const rate = this.new_coin.rate;

      if (!coin) {
        return this.$swal.fire({
          icon: 'warning',
          title: 'Campos em falta',
          text: 'A moeda é obrigatória.',
        });
      }

      const ok = await this.confirmAdd(`Adicionar a moeda ${coin}?`);
      if (!ok) return;

      this.loading = true;
      try {
        await axios.post(`${this.$store.state.apiUrl}feedCountry/coins`, {
          coin,
          rate,
        });

        this.$swal({
          title: 'Moeda Adicionada!',
          icon: 'success',
          timer: 1200,
        });

        this.new_coin = { coin: '', rate: 1 };

        await this.fetchCoins();
        this.$refs['modal_add-coin'].hide();
      } catch (e) {
        console.error('Error adding coin:', e);
        this.$swal?.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível adicionar a moeda',
        });
      } finally {
        this.loading = false;
      }
    },

    // ============== COUNTRIES & LABELS ==============
    async fetchCountries() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}countrys/all`
        );
        this.countries = data.data || [];
      } catch (e) {
        console.error('Error fetching countries:', e);
      }
    },

    async fetchCustomLabels() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}feedCountry/custom_labels`
        );
        this.custom_values = data.data || [];
      } catch (e) {
        console.error('Error fetching custom labels:', e);
      }
    },
    openCustomValuesModal() {
      this.fetchCustomLabels().then(() =>
        this.$refs['modal_add-custom-values'].show()
      );
    },
    async updateCustomValues(label) {
      try {
        await axios.patch(
          `${this.$store.state.apiUrl}feedCountry/custom_labels`,
          {
            name: label.name,
            min: label.min,
            med: label.med,
            max: label.max,
            weight: label.weight,
          }
        );
        this.$swal({
          title: 'Valores Atualizados!',
          icon: 'success',
          timer: 1200,
        });
        await this.fetchFeeds();
      } catch (e) {
        console.error('Error updating custom values:', e);
      }
    },

    // ============== GENERATE ==============
    async generateFeeds() {
      try {
        await axios.post(
          `${this.$store.state.apiUrl}feedCountry/generate`,
          null
        );

        await axios.post(`${this.$store.state.apiUrl}shopify/generate`, null);

        this.$swal({ title: 'Feeds Gerados!', icon: 'success', timer: 1200 });
      } catch (e) {
        console.error('Error generating feeds:', e);
      }
    },
  },

  async created() {
    await this.reloadAll();
  },
};
</script>

<style scoped>
/* layout */
.productslist {
  position: relative;
  padding-bottom: 40px;
}
.container-tight {
  max-width: 1100px;
  margin: 0 auto;
}
/* full width só para feeds */
.container-wide {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 16px;
}

/* header */
.header {
  border-bottom: 1px solid #eef2f6;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}
.appmark {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: radial-gradient(100% 100% at 30% 20%, #7db3ff 0%, #3a77b4 100%);
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 800;
  letter-spacing: 0.5px;
  box-shadow: 0 6px 18px rgba(58, 119, 180, 0.25);
}
.page-title {
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: 0.2px;
}
.badge-soft {
  background: #eef5fb;
  color: #3a77b4;
  padding: 6px 10px;
  font-weight: 600;
  border-radius: 999px;
}

/* segmented (menu centrado) — mais espaçamento */
.segmented {
  display: flex;
  gap: 10px;
  padding: 10px;
  margin: 18px auto 28px;
  border-radius: 14px;
  background: #f3f6fb;
  box-shadow: inset 0 0 0 1px #e6edf5;
}
.segmented-wide {
  max-width: 560px;
}
.segmented-item {
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #223;
  padding: 10px 20px;
  font-weight: 700;
  letter-spacing: 0.2px;
  transition: transform 0.15s ease, background 0.15s ease;
}
.segmented-item:hover {
  transform: translateY(-1px);
  background: #eaf2fb;
}
.segmented-item.active {
  background: #3a77b4;
  color: #fff;
  box-shadow: 0 8px 18px rgba(58, 119, 180, 0.28);
}

/* cards & tables (base) */
.card {
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.06);
}
.table {
  font-size: 13px;
}
.table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7fafc;
  border-top: 0;
  border-bottom: 1px solid #e6edf5;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: #62748a;
}
.table tbody tr:hover {
  background: #fbfdff;
}
.table tbody tr:nth-child(2n) {
  background: #fcfdff;
}

/* ---------- FEEDS header (grid 3 colunas) ---------- */
.feeds-card__header {
  display: grid;
  grid-template-columns: 1fr minmax(280px, 640px) auto;
  grid-gap: 16px;
  align-items: center;
  padding: 4px 2px 6px;
}
.fch-left {
  display: flex;
  align-items: center;
  min-width: 0;
}
.fch-center {
  width: 100%;
}
.fch-right {
  display: flex;
  align-items: center;
}

/* ---------- tabela feeds ---------- */
.feeds-card__title {
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.2px;
}
.feeds-table .w-id {
  width: 84px;
}
.feeds-table .w-name {
  min-width: 260px;
}
.feeds-table .w-chips {
  min-width: 220px;
}
.feeds-table .w-xs {
  width: 92px;
}
.feeds-table .w-sm {
  width: 120px;
}
.feeds-table .w-actions {
  width: 96px;
}
.feed-row td {
  vertical-align: middle;
}

/* avatar inicial do feed */
.feed-avatar {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: radial-gradient(100% 100% at 30% 20%, #7db3ff 0%, #3a77b4 100%);
  color: #fff;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.2px;
  box-shadow: 0 6px 12px rgba(58, 119, 180, 0.18);
}

/* separador • */
.dot-sep {
  margin: 0 6px;
  opacity: 0.35;
}

/* chips (países/categorias) */
.cell-chips {
  white-space: nowrap;
  max-width: 100%;
}
.chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  margin: 2px 4px 2px 0;
  vertical-align: middle;
}
.chip-soft {
  background: #eef5fb;
  color: #2f6aa1;
  border: 1px solid #d9e7f5;
}
.chip-outline {
  background: #fff;
  color: #556678;
  border: 1px dashed #cfd9e6;
}
.chip-more {
  background: #f2f4f7;
  color: #6b7785;
  border: 1px solid #e2e7ee;
}

/* pills (stock) */
.pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}
.pill-green {
  background: #e9f7ef;
  color: #1a7f3c;
  border: 1px solid #cdebd7;
}
.pill-gray {
  background: #f3f5f7;
  color: #6b7785;
  border: 1px solid #e2e7ee;
}

/* botões */
.btn-square {
  width: 30px;
  height: 30px;
  display: inline-grid;
  place-items: center;
  padding: 0;
  border-radius: 8px;
}
.btn-ghost {
  border: 1px solid #e6edf5;
  background: #fff;
}
.btn-ghost:hover {
  background: #f6f9fd;
}

/* texto monoespaçado */
.text-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
}

/* empty state */
.empty-state {
  text-align: center;
}
.empty-ico {
  font-size: 28px;
  opacity: 0.5;
}
.empty-title {
  font-weight: 800;
  margin-top: 6px;
}
.empty-sub {
  color: #6c7a89;
  margin-top: 2px;
}

/* sort header */
.th-sort {
  cursor: pointer;
  user-select: none;
}
.th-sort .sort-caret {
  margin-left: 6px;
  font-size: 11px;
  color: #7a8aa0;
}

/* loading */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 1050;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* utils */
.gap-2 > * + * {
  margin-left: 0.5rem;
}
.hero-left {
  min-width: 0;
}
</style>
